import { _ } from "libs";

import React, { useContext, useEffect, useRef, useState } from "react";
import SubPageLayout from "./subPageLayout";
import ReservationPageContext from "./reservationPageContext";
import PlayerItemSmallColumn from "../../items/player/smallColumn";
import PlayerItemMedium from "../../items/player/medium";
import SearchInput from "../../searchInput";
import { Player, User } from "../../../types";
import { InvisibleInfiniteLoader } from "../../infiniteLoader";
import { formatName } from "../../../libs/formatName";
import Loader from "../../loader";
import { loadFriendsWithoutDispatch, openPlayerPopup, playerProfiles } from "../../../actions";
import cx from "classnames";
import { first } from "rxjs/operators";

const PAGE_LIMIT = 50;

const PartnerSelect = () => {
	const pageContext = useContext(ReservationPageContext);
	const {
		setPage,
		clubInfo,
		clubDetails,
		reservation: { params, context },
		getReservationPartnerSuggestions,
		getReservationContext,
		addPartnerToReservation,
		socialFind,
		goBack,
	} = pageContext;

	const initialState = [] as User[];

	const [loadingMembers, setLoadingMembers] = useState(false);
	const [loadingSearchResults, setLoadingSearchResults] = useState(false);
	const [loadingBuddies, setLoadingBuddies] = useState(false);
	const [currentPage, setScrollPage] = useState(0);
	const [hasMoreMemberResults, setHasMoreMemberResults] = useState(true);
	const [members, setMembers] = useState([]);
	const [buddies, setBuddies] = useState([]);
	const [suggestions, updateSuggestions] = useState(initialState);
	const [searchState, updateSearchState] = useState({ query: "", results: initialState });
	const [currentSearchPage, setCurrentSearchPage] = useState(0);
	const [hasMoreSearchResults, setHasMoreSearchResults] = useState(true);

	const firstLoad = useRef(true);
	const loggedUserIsMember = useRef(pageContext.clubDetails.memberStatus === "member");

	useEffect(() => {
		if (firstLoad.current) {
			firstLoad.current = false;
			loadBuddies(0);
			getReservationPartnerSuggestions(clubInfo.clubId, params.courtId, params.startDateTime).then(
				(suggestions) => {
					updateSuggestions(suggestions);
				}
			);
		} else {
			firstLoad.current = false;
		}
	}, []);

	useEffect(() => {
		if (firstLoad.current) {
			if (["private", "mixed"].includes(clubDetails.type) && loggedUserIsMember.current) {
				firstLoad.current = false;
				loadMembers(0);
			}
		} else {
			firstLoad.current = false;
		}
	}, []);

	const loadMembers = async (page: number) => {
		setLoadingMembers(true);

		try {
			const { data, pageRows, pageCurrent } = await socialFind(
				{ clubId: clubInfo.clubId, includeFavorites: false },
				page,
				PAGE_LIMIT
			);
			const newMembers = pageCurrent === 0 ? [...data] : [...members, ...data];
			setMembers(newMembers);

			if (newMembers.length < pageRows) {
				setScrollPage(currentPage + 1);
			} else {
				setHasMoreMemberResults(false);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoadingMembers(false);
		}
	};

	const loadBuddies = async (page: number) => {
		setLoadingBuddies(true);
		try {
			const res = await loadFriendsWithoutDispatch({
				pageCurrent: page,
				pageLimit: 100,
				clubId: clubInfo.clubId,
				timestamp: params.startDateTime,
				courtId: params.courtId,
			});
			setBuddies(res.data);
		} catch (error) {
			console.log(error);
		} finally {
			setLoadingBuddies(false);
		}
	};

	const onMembersViewChange = async (inView: boolean, observerEntry) => {
		const shouldLoad = inView && hasMoreMemberResults;

		if (shouldLoad) {
			loadMembers(currentPage);
		}
	};

	const onBuddiesViewChange = async (inView: boolean, observerEntry) => {
		//do nothing
	};

	const onSearchViewChange = async (inView: boolean, observerEntry) => {
		const shouldLoad = inView && hasMoreSearchResults && !loadingSearchResults;

		if (shouldLoad) {
			onInputChange();
		}
	};

	const onInputChange = (query?: string) => {
		setLoadingSearchResults(true);
		if (query) {
			setCurrentSearchPage(0);
			setHasMoreSearchResults(true);
		} else {
			query = searchState.query;
		}
		playerProfiles({ clubId: clubInfo.clubId, term: query ?? searchState.query }, currentSearchPage, 20)
			.then((results) => {
				const { data, pageRows } = results;
				const newResults = currentSearchPage === 0 ? [...data] : [...searchState.results, ...data];
				updateSearchState({ query, results: newResults });
				if (newResults.length < pageRows) {
					setCurrentSearchPage(currentSearchPage + 1);
				} else {
					setHasMoreSearchResults(false);
				}
			})
			.catch((err) => {
				console.log("aborting");
				//ignore
			})
			.finally(() => {
				setLoadingSearchResults(false);
			});
	};

	const getMembershipLabel = (player: Player): boolean | null => {
		if (pageContext.clubDetails.type === "public") {
			// Case 1
			// console.log("Case 1");
			return null;
		}

		if (pageContext.clubDetails.type === "mixed") {
			if (loggedUserIsMember.current) {
				// Case 2
				// console.log("Case 2");
				return player.type === "player";
			}
		}

		if (
			["mixed", "private"].includes(pageContext.clubDetails.type) &&
			pageContext.clubDetails.guestsGenerallyAllowed === true
		) {
			if (loggedUserIsMember.current && pageContext.reservation.context.guestsAllowed === true) {
				// Case 3
				// console.log("Case 3");
				// Members of the mixed club with active membership are returned with type player instead of member
				return player.type === "player";
			}

			if (loggedUserIsMember.current && pageContext.reservation.context.guestsAllowed === false) {
				// Case 4
				// console.log("Case 4");
				// Members of the mixed club with active membership are returned with type player instead of member
				return player.type === "player";
			}
		}

		if (
			pageContext.clubDetails.type === "private" &&
			(pageContext.clubDetails.guestsGenerallyAllowed === false ||
				pageContext.reservation.context.guestsAllowed === false)
		) {
			if (loggedUserIsMember.current) {
				// Case 5
				// console.log("Case 5");
				// Members of the private club with active membership are returned with type player instead of member
				return player.type === "player";
			}
		}

		// console.log("NO CASE APPLIED");
		return null;
	};

	const selectUserForReservation = (user: User) => {
		addPartnerToReservation(user, user.type ?? "player");
		getReservationContext({});
		goBack();
	};

	const openPlayerModal = (user: User) => {
		openPlayerPopup(user.playerId, false, null, {
			selectForReservation: () => {
				selectUserForReservation(user);
			},
		});
	};

	const Suggestions = () => {
		if (!Array.isArray(suggestions) || (Array.isArray(suggestions) && suggestions.length <= 0)) {
			return null;
		}
		return (
			<div className="group mt-4">
				<h5>{_("reservation.recommendedPlayers")}</h5>
				<div className="recommendedPlayers fourPlayerColumnsWrapper">
					{suggestions.map((user, index) => (
						<div
							className="cell"
							key={index}
							onClick={() => {
								selectUserForReservation(user);
							}}
						>
							<PlayerItemSmallColumn
								name={user.fullName ? user.fullName : formatName(user.firstName, user.lastName)}
								photoUrl={user.photoUrl}
							/>
						</div>
					))}
				</div>
			</div>
		);
	};

	const Buddies = () => {
		let displayMemberLabels = clubDetails.type !== "public";
		let guestDisabled =
			(clubDetails.type === "mixed" || clubDetails.type === "private") &&
			clubDetails.guestsGenerallyAllowed &&
			clubDetails.memberStatus === "member" &&
			pageContext.reservation.context.guestsAllowed === false;
		let strictlyPrivate = clubDetails.type === "private" && clubDetails.guestsGenerallyAllowed === false;
		return (
			<div className="group mt-4">
				<h5>{_("reservation.buddies")}</h5>
				<div className="table looseSpacing">
					{buddies.map((user, index) => {
						if (strictlyPrivate && user.type === "guest") return;
						let isItGuestAndShouldBeDisabled = user.type === "guest" && guestDisabled;
						return (
							<div
								className={cx(
									"cell",
									{ clickable: !isItGuestAndShouldBeDisabled },
									{ disabled: isItGuestAndShouldBeDisabled }
								)}
								key={index}
								onClick={(event) => {
									if (isItGuestAndShouldBeDisabled) return;
									addPartnerToReservation(user, user.type ?? "player");
									getReservationContext({});
									goBack();
								}}
							>
								<PlayerItemMedium
									name={user.fullName ? user.fullName : formatName(user.firstName, user.lastName)}
									meta={user.city}
									photoUrl={user.photoUrl}
									isMember={displayMemberLabels ? getMembershipLabel(user) : null}
								/>
							</div>
						);
					})}
					{(!buddies || buddies.length === 0) && !loadingBuddies ? (
						<div className="noResults">{_("players.location.noResults.heading")}</div>
					) : null}
					<InvisibleInfiniteLoader onViewChange={onBuddiesViewChange} loading={loadingBuddies} />
				</div>
			</div>
		);
	};

	const PlayerWithoutAccount = () => {
		if (!context.guestsAllowed) {
			return null;
		}

		return (
			<div className="group">
				<h5>{_("reservation.playerWithoutAccount.heading")}</h5>
				<a onClick={(e) => setPage("playerWithoutAccount")} className="cell freeEntry">
					<div className="avatar">
						<div className="icon"></div>
					</div>
					<div className="title">{_("reservation.playerWithoutAccount.description")}</div>
				</a>
			</div>
		);
	};

	const SearchedPlayerWithoutAccount = () => {
		if (!context.guestsAllowed) {
			return null;
		}

		return (
			<div className="group">
				<a onClick={(e) => setPage("playerWithoutAccount")} className="cell freeEntry">
					<div className="avatar">
						<div className="icon"></div>
					</div>
					<div className="text">
						<div className="title">{searchState.query}</div>
						<div className="meta">{_("reservation.playerWithoutAccount.searchResultDescription")}</div>
					</div>
				</a>
			</div>
		);
	};

	const ClubMembers = () => {
		if (!loggedUserIsMember.current) {
			return null;
		}
		return (
			<div className="group">
				<h5>{_("reservation.clubMembers")}</h5>
				<div className="table looseSpacing">
					{members.map((user, key) => (
						<div
							className="cell clickable"
							key={key + user.userId}
							onClick={(e) => {
								addPartnerToReservation(user, user.type ?? "player");
								getReservationContext({});
								goBack();
							}}
						>
							<PlayerItemMedium
								name={user.fullName ? user.fullName : user.firstName + " " + user.lastName}
								meta={user.city}
								photoUrl={user.photoUrl}
								isMember={user.type === "player"}
							/>
						</div>
					))}
					{(!members || members.length === 0) && !loadingMembers ? (
						<div className="noResults">{_("players.location.noResults.heading")}</div>
					) : null}
					<InvisibleInfiniteLoader onViewChange={onMembersViewChange} loading={loadingMembers} />
				</div>
			</div>
		);
	};

	const GuestInfo = () => {
		if (!context.guestsAllowed && pageContext.clubDetails.guestsGenerallyAllowed === true) {
			return <div className="msg info mb-4">{_("error.guestNotAllowed")}</div>;
		}
		if (
			pageContext.clubDetails.type === "private" &&
			pageContext.clubDetails.guestsGenerallyAllowed === false &&
			searchState.query !== ""
		) {
			return <div className="msg info mb-4">{_("error.searchResultsOnlyMembers")}</div>;
		}

		return null;
	};

	const SearchResults = () => {
		return (
			<>
				<GuestInfo />
				<SearchedPlayerWithoutAccount />
				{searchState.results.map((user) => {
					// For Case 5, we don't display non-member players in the search results
					if (
						pageContext.clubDetails.type === "private" &&
						pageContext.clubDetails.guestsGenerallyAllowed === false &&
						user.type === "guest"
					) {
						return null;
					}
					return (
						<div
							className={cx("cell", "clickable", {
								disabled: !context.guestsAllowed && user.type === "guest",
							})}
							key={user.userId}
							onClick={
								!context.guestsAllowed && user.type === "guest"
									? null
									: (e) => {
											addPartnerToReservation(user, user.type ?? "player");
											getReservationContext({});
											goBack();
									  }
							}
						>
							<PlayerItemMedium
								key={user.userId}
								name={user.fullName ? user.fullName : user.firstName + " " + user.lastName}
								meta={user.city}
								photoUrl={user.photoUrl}
								isMember={getMembershipLabel(user)}
								disclosureButton={
									!context.guestsAllowed && user.type === "guest" ? null : () => openPlayerModal(user)
								}
							/>
						</div>
					);
				})}
				{loadingSearchResults && <Loader />}
				<InvisibleInfiniteLoader onViewChange={onSearchViewChange} />
			</>
		);
	};

	return (
		<SubPageLayout title={_("reservation.players.title")}>
			<div className="wizardBody">
				<div className="searchBox">
					<SearchInput onChange={(query) => onInputChange(query)} />
				</div>
				<div className="searchResults">
					{!searchState.query ? (
						<>
							<GuestInfo />
							<Suggestions />
							<Buddies />
							<PlayerWithoutAccount />
							<ClubMembers />
						</>
					) : (
						<div className="table looseSpacing">
							<SearchResults />
						</div>
					)}
				</div>
			</div>
		</SubPageLayout>
	);
};
export default PartnerSelect;
